/**
 * DevOps Landing Page - SEO Optimized
 * Route: /devops
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronRight, Terminal, Server, GitBranch, Shield, Award, Lightbulb, Zap, Star, CheckCircle, Users, BookOpen, Code } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';

const GRRAS_LOGO = '/images/grras-logo.png';

const DevOpsLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const { isIndia } = useCurrency();
  const fp = (inr, usd) => isIndia ? `\u20B9${inr.toLocaleString('en-IN')}` : `$${usd}`;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const tools = [
    { name: 'Docker', labs: 18, level: 'Container Pro', color: 'from-blue-500 to-cyan-500', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg' },
    { name: 'Kubernetes (k3s)', labs: 18, level: 'K8s DevOps', color: 'from-blue-600 to-indigo-600', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg' },
    { name: 'Ansible', labs: 18, level: 'Automation Pro', color: 'from-red-500 to-orange-500', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg' },
    { name: 'Terraform', labs: 18, level: 'Cloud & IaC', color: 'from-purple-500 to-violet-600', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg' },
    { name: 'Git & GitHub', labs: 18, level: 'Version Control', color: 'from-orange-500 to-red-500', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
    { name: 'CI/CD Pipeline', labs: 15, level: 'DevOps Engineer', color: 'from-green-500 to-emerald-600', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jenkins/jenkins-original.svg' },
  ];

  const features = [
    { title: 'Real VM Environment', desc: 'Isolated RHEL virtual machine for each student', icon: Server },
    { title: 'Auto-Verified Tasks', desc: 'Instant pass/fail on every command you run', icon: CheckCircle },
    { title: 'Command Hints', desc: 'Stuck? Get hints without leaving the lab', icon: Lightbulb },
    { title: 'Real-World Projects', desc: 'CI/CD, AWS, Docker, Kubernetes industry projects', icon: Code },
    { title: 'Mock Tests', desc: '46 tests, 895+ questions for interview prep', icon: BookOpen },
    { title: 'Lifetime Access', desc: 'Purchase once, practice forever', icon: Shield },
  ];

  const testimonials = [
    { name: 'Rahul Sharma', role: 'RHCSA Certified', quote: 'Cleared RHCSA in first attempt. The real VM practice made all the difference.' },
    { name: 'Priya Singh', role: 'Docker Pro', quote: 'Docker and Kubernetes labs are excellent. Got placed in 2 months.' },
    { name: 'Amit Kumar', role: 'DevOps Engineer', quote: 'Best DevOps practice platform in India. Worth every rupee.' },
    { name: 'Sneha Patel', role: 'DevOps Engineer', quote: 'Auto-verified tasks helped me know exactly where I was going wrong.' },
  ];

  const faqs = [
    { q: 'Do I need prior experience to start DevOps labs?', a: 'No prior experience needed. We have beginner to advanced level labs. Start with Git and Linux basics.' },
    { q: 'What DevOps tools can I practice?', a: 'Docker, Kubernetes, Ansible, Terraform, Git, CI/CD Pipeline and more — all on real RHEL virtual machines.' },
    { q: 'Is there a free trial available?', a: 'Yes! Register for free and access select labs without any payment. No credit card required.' },
    { q: 'How is this different from YouTube tutorials?', a: 'You actually type commands on a real VM and get instant pass/fail feedback. It\'s exam-like practice, not passive watching.' },
    { q: 'Will I get a certificate after completing labs?', a: 'Yes, course completion certificates are provided. These labs also prepare you for Docker, Kubernetes and Red Hat certifications.' },
    { q: 'How long do I have access after purchase?', a: 'Lifetime access. Purchase once and practice anytime, anywhere, forever.' },
  ];

  return (
    <>
      <Helmet>
        <title>DevOps Hands-On Labs | Docker, Kubernetes, Ansible Practice | GRRAS</title>
        <meta name="description" content="Practice DevOps tools on real Virtual Machines. Docker, Kubernetes, Ansible, Terraform labs with auto-verified tasks. Free signup. Powered by GRRAS Solutions." />
        <meta name="keywords" content="devops lab online, docker practice lab, kubernetes hands on, ansible training online, devops course india, devops tools practice, real vm devops lab" />
        <link rel="canonical" href="https://lab.grras.tech/devops" />
        <meta property="og:title" content="DevOps Hands-On Labs — Real VM Practice | GRRAS Lab" />
        <meta property="og:description" content="Practice Docker, Kubernetes, Ansible & more on real RHEL VMs. Auto-verified tasks, hints & projects. Free signup at GRRAS Lab." />
        <meta property="og:image" content={GRRAS_LOGO} />
        <meta property="og:url" content="https://lab.grras.tech/devops" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "DevOps Hands-On Labs",
          "description": "Practice Docker, Kubernetes, Ansible, Terraform on real RHEL VMs with auto-verified tasks.",
          "provider": { "@type": "Organization", "name": "GRRAS Solutions Pvt. Ltd.", "url": "https://lab.grras.tech" },
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR", "availability": "https://schema.org/InStock" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "GRRAS Solutions Pvt. Ltd.",
          "url": "https://lab.grras.tech",
          "logo": GRRAS_LOGO,
          "foundingDate": "2008",
          "contactPoint": { "@type": "ContactPoint", "telephone": "+91-9001991227", "email": "info@grras.tech" }
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a] text-white font-sans">
        {/* Navbar */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-lg shadow-lg border-b border-white/5' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <a href="https://lab.grras.tech" className="flex items-center gap-2">
              <img src={GRRAS_LOGO} alt="GRRAS Solutions Logo" className="h-8 sm:h-10" />
            </a>
            <div className="hidden md:flex items-center gap-8 text-sm text-gray-300">
              <a href="https://lab.grras.tech" className="hover:text-white transition-colors">Home</a>
              <a href="#tools" className="hover:text-white transition-colors">Courses</a>
              <a href="https://lab.grras.tech/pricing" className="hover:text-white transition-colors">Pricing</a>
              <a href="https://lab.grras.tech/login" className="hover:text-white transition-colors">Login</a>
            </div>
            <a href="https://lab.grras.tech/register" className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:scale-105">
              Start Free
            </a>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-red-900/10 via-transparent to-transparent"></div>
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-red-600/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>
          
          <div className="max-w-5xl mx-auto text-center relative z-10">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Master DevOps with{' '}
              <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Real Hands-On Labs</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
              Practice Docker, Kubernetes, Ansible, Terraform & more on real isolated Virtual Machines — not just theory. Auto-verified tasks with instant feedback.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a href="https://lab.grras.tech/register" className="bg-red-600 hover:bg-red-700 text-white px-8 py-3.5 rounded-lg font-semibold text-base transition-all hover:scale-105 shadow-lg shadow-red-600/20 flex items-center gap-2">
                Start Free Now <ChevronRight className="w-4 h-4" />
              </a>
              <a href="https://lab.grras.tech/pricing" className="border border-white/20 hover:border-white/40 text-white px-8 py-3.5 rounded-lg font-semibold text-base transition-all hover:bg-white/5">
                View Pricing
              </a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 max-w-3xl mx-auto">
              {[
                { value: '500+', label: 'Students Trained' },
                { value: '249+', label: 'Hands-On Labs' },
                { value: '16', label: 'Courses Available' },
                { value: '95%', label: 'Pass Rate' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DevOps Tools */}
        <section id="tools" className="py-16 sm:py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">All DevOps Tools — One Platform</h2>
              <p className="text-gray-400 text-base">Real VM environment for every tool. No setup required.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {tools.map((tool, i) => (
                <div key={i} className="group bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:bg-white/[0.06] hover:border-white/20 transition-all hover:-translate-y-1">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform p-2">
                    <img src={tool.logo} alt={`${tool.name} logo`} className="w-8 h-8" loading="lazy" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{tool.name}</h3>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-400">{tool.labs} Labs</span>
                    <span className="bg-white/10 text-gray-300 px-2 py-0.5 rounded text-xs">{tool.level}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 sm:py-24 px-4 bg-white/[0.02]">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">How It Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
              {[
                { step: '1', title: 'Register Free', desc: 'Create your account in 60 seconds', icon: Users },
                { step: '2', title: 'Choose Your Lab', desc: 'Pick any DevOps tool to practice', icon: Terminal },
                { step: '3', title: 'Practice on Real VM', desc: 'Get instant feedback on every task', icon: Server },
              ].map((item, i) => (
                <div key={i} className="relative text-center">
                  <div className="w-16 h-16 rounded-full bg-red-600/10 border-2 border-red-600/30 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-red-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                  {i < 2 && <ChevronRight className="hidden md:block absolute top-8 -right-2 w-6 h-6 text-gray-600" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why GRRAS Lab */}
        <section className="py-16 sm:py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">Why 500+ Students Choose GRRAS Lab</h2>
            <p className="text-gray-400 text-center mb-12 text-base">Everything you need to master DevOps</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {features.map((f, i) => (
                <div key={i} className="bg-white/[0.03] border border-white/10 rounded-xl p-5 hover:border-red-500/30 transition-all">
                  <f.icon className="w-8 h-8 text-red-500 mb-3" />
                  <h3 className="font-semibold text-white mb-1">{f.title}</h3>
                  <p className="text-sm text-gray-400">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 sm:py-24 px-4 bg-white/[0.02]">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Simple, Transparent Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Free */}
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 flex flex-col">
                <h3 className="text-lg font-semibold mb-1">Free Start</h3>
                <p className="text-3xl font-bold mb-4">{fp(0, 0)}</p>
                <ul className="space-y-2 text-sm text-gray-400 mb-6 flex-1">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Select free labs</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Mock test preview</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />No credit card needed</li>
                </ul>
                <a href="https://lab.grras.tech/register" className="block text-center border border-white/20 hover:border-white/40 text-white py-2.5 rounded-lg font-medium transition-all hover:bg-white/5">Start Free</a>
              </div>
              {/* Individual */}
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 flex flex-col">
                <h3 className="text-lg font-semibold mb-1">Individual Course</h3>
                <p className="text-3xl font-bold mb-4">{fp(799, 19)}<span className="text-sm text-gray-400 font-normal">/course</span></p>
                <ul className="space-y-2 text-sm text-gray-400 mb-6 flex-1">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />15-18 labs per course</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Real VM environment</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Lifetime access</li>
                </ul>
                <a href="https://lab.grras.tech/pricing" className="block text-center border border-white/20 hover:border-white/40 text-white py-2.5 rounded-lg font-medium transition-all hover:bg-white/5">Buy Now</a>
              </div>
              {/* All Access */}
              <div className="bg-gradient-to-b from-red-900/20 to-red-950/10 border-2 border-red-500/40 rounded-2xl p-6 flex flex-col relative">
                <span className="absolute -top-3 right-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">BEST VALUE</span>
                <h3 className="text-lg font-semibold mb-1">All Access Lifetime</h3>
                <p className="text-3xl font-bold mb-4">{fp(4999, 99)}<span className="text-sm text-gray-400 font-normal"> one-time</span></p>
                <ul className="space-y-2 text-sm text-gray-300 mb-6 flex-1">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />16 Courses, 249+ Labs</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />All future courses FREE</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Mock Tests + Projects</li>
                </ul>
                <a href="https://lab.grras.tech/pricing" className="block text-center bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold transition-all">Get All Access</a>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 sm:py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">What Our Students Say</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-yellow-500 fill-yellow-500" />)}
                  </div>
                  <p className="text-sm text-gray-300 mb-4 leading-relaxed">"{t.quote}"</p>
                  <div>
                    <p className="font-semibold text-white text-sm">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-24 px-4 bg-white/[0.02]">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-white/10 rounded-xl overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.03] transition-colors">
                    <span className="font-medium text-sm sm:text-base pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-sm text-gray-400 leading-relaxed">{faq.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-16 sm:py-24 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Start Your DevOps Journey?</h2>
            <p className="text-gray-400 mb-8">Join 500+ students already practicing on real VMs. Free to start.</p>
            <a href="https://lab.grras.tech/register" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3.5 rounded-lg font-semibold text-base transition-all hover:scale-105 shadow-lg shadow-red-600/20">
              Create Free Account <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-10 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <img src={GRRAS_LOGO} alt="GRRAS Solutions" className="h-8" />
                <span className="text-xs text-gray-500">Authorized Red Hat Partner since 2008</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
                <a href="https://lab.grras.tech" className="hover:text-white transition-colors">Home</a>
                <a href="https://lab.grras.tech/pricing" className="hover:text-white transition-colors">Pricing</a>
                <a href="https://lab.grras.tech/login" className="hover:text-white transition-colors">Login</a>
                <a href="https://lab.grras.tech/register" className="hover:text-white transition-colors">Register</a>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
              <p>&copy; 2026 GRRAS Solutions Pvt. Ltd. All rights reserved.</p>
              <div className="flex items-center gap-4">
                <span>info@grras.tech</span>
                <span>+91-9001991227</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default DevOpsLanding;
